import { useEffect, useMemo, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box } from '@mui/material';
import { max, min, uniq } from 'lodash';
import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import { ProductSalesReportFilter } from '../../interfaces/Report/ProductSalesReport/ProductSalesReportFilter';
import { ProductSalesReport } from '../../interfaces/Report/ProductSalesReport/ProductSalesReport';
import { getProductSalesReport } from '../../api/Report';
import { Waiting } from '../../components/Waiting/Waiting';
import {
  areInSamePeriod,
  getTicksBetweenRangeByPeriod,
} from '../../utils/Period';
import { formatLocalTime } from '../../utils/Date';
import { Period, periodToDateFormatMap } from '../../interfaces/Period';

type ProductSalesChartProps = {
  filters: ProductSalesReportFilter;
};

export const ProductSalesChart = ({ filters }: ProductSalesChartProps) => {
  const [data, setData] = useState<ProductSalesReport[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductSalesReport({ ...filters, pagination: undefined })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [filters]);

  const [options, series] = useMemo(() => {
    const period = filters.period ?? Period.HOUR;

    const start = min(data.map((x) => x.createdAt)) ?? new Date();
    const end = max(data.map((x) => x.createdAt)) ?? new Date();

    const ticks = getTicksBetweenRangeByPeriod(start, end, period);

    const opts: ApexOptions = {
      chart: {
        type: 'bar',
        zoom: {
          enabled: false,
        },
        stacked: true,
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        hideEmptySeries: true,
      },
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
        },
      },
      xaxis: {
        categories: ticks.map((tick) =>
          formatLocalTime(tick, periodToDateFormatMap[period]),
        ),
        title: {
          text: 'Tarih',
        },
      },
    };

    const formatLabel = (x: ProductSalesReport) =>
      x.brandName ? `${x.brandName} - ${x.productName}` : x.productName;

    const yLabels = uniq(data.map(formatLabel));

    const seriesData = yLabels.map((yLabel) => ({
      name: yLabel,
      data: ticks.map(() => 0),
    }));

    data.forEach((x) => {
      seriesData[yLabels.indexOf(formatLabel(x))].data[
        ticks.findIndex((tick) => areInSamePeriod(x.createdAt, tick, period))
      ] += x.amount;
    });

    return [opts, seriesData];
  }, [data, filters]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 450,
      }}
    >
      <Waiting open={loading} />

      <Chart
        options={options}
        series={series}
        type='bar'
        width='100%'
        height='100%'
      />
    </Box>
  );
};
